import { useState, useEffect } from 'react'
import { apiService, ApiError } from '../services/api'
import { UserPhone } from '../types'

interface User {
 id: string
 name: string
 email: string
 phones: UserPhone[]
 role: {
 id: string
 name: string
 description: string
 }
 status: 'active' | 'inactive'
 lastLogin?: string
 createdAt: string
 avatar?: string
 dealershipRole?: string
 isActive?: boolean
}

interface UseUsersResult {
 users: User[]
 isLoading: boolean
 error: string | null
 refetch: () => Promise<void>
 createUser: (data: {
 name: string
 email: string
 password: string
 phones: UserPhone[]
 dealershipRole: string
 isActive?: boolean
 }) => Promise<User>
 disableUser: (userId: string) => Promise<void>
 activateUser: (userId: string) => Promise<void>
 getUserById: (userId: string) => Promise<User>
 updateUser: (userId: string, userData: {
 name: string
 email: string
 password?: string
 phones: UserPhone[]
 dealershipRole: string
 isActive?: boolean
 }) => Promise<void>
 loadMore: () => Promise<void>
 hasMore: boolean
 currentPage: number
 pagination: {
 page: number
 limit: number
 total: number
 totalPages: number
 } | null
}

export const useUsers = (): UseUsersResult => {
 const [users, setUsers] = useState<User[]>([])
 const [isLoading, setIsLoading] = useState(true)
 const [error, setError] = useState<string | null>(null)
 const [currentPage, setCurrentPage] = useState(1)
 const [hasMore, setHasMore] = useState(true)
 const [pagination, setPagination] = useState<{
 page: number
 limit: number
 total: number
 totalPages: number
 } | null>(null)

 const fetchUsers = async (page: number = 1, append: boolean = false) => {
 try {
 if (!append) {
 setIsLoading(true)
 }
 setError(null)
 
 const response = await apiService.getUsers(100, page)
 const responseData = (response as any).data || response
 
 // A API retorna { users: [...], pagination: {...} }
 const usersData = responseData.users || []
 const paginationData = responseData.pagination
 
 if (append) {
 setUsers(prev => [...prev, ...usersData])
 } else {
 setUsers(usersData)
 }
 
 setPagination(paginationData)
 
 // Verifica se há mais páginas usando a informação de paginação
 setHasMore(paginationData ? paginationData.page < paginationData.totalPages : false)
 
 } catch (err) {
 const errorMessage = err instanceof ApiError 
 ? err.message 
 : 'Erro ao carregar usuários'
 
 setError(errorMessage)
 console.error('Erro ao buscar usuários:', err)
 
 if (!append) {
 setUsers([])
 }
 } finally {
 setIsLoading(false)
 }
 }

 const createUser = async (data: {
 name: string
 email: string
 password: string
 phones: UserPhone[]
 dealershipRole: string
 isActive?: boolean
 avatar?: File | null
 }) => {
 try {
 const response = await apiService.createUser(data)
 const responseData = (response as any).data || response
 
 // Adiciona o novo usuário à lista
 setUsers(prev => [...prev, responseData])
 
 return responseData
 } catch (err) {
 const errorMessage = err instanceof ApiError 
 ? err.message 
 : 'Erro ao criar usuário'
 
 throw new Error(errorMessage)
 }
 }

 const disableUser = async (userId: string) => {
 try {
 console.log('🔄 Iniciando inativação do usuário:', userId)
 const response = await apiService.disableUser(userId)
 console.log('✅ Resposta da API disableUser:', response)
 
 // Atualiza o usuário na lista local
 setUsers(prev => prev.map(user => 
 user.id === userId 
 ? { ...user, isActive: false, status: 'inactive' }
 : user
 ))
 
 console.log('✅ Usuário inativado com sucesso')
 
 } catch (err) {
 console.error('❌ Erro detalhado ao inativar usuário:', err)
 
 let errorMessage = 'Erro ao inativar usuário'
 
 if (err instanceof ApiError) {
 console.error('❌ ApiError:', {
 status: err.status,
 message: err.message,
 data: (err as any).data
 })
 errorMessage = err.message
 } else if (err instanceof Error) {
 console.error('❌ Error:', err.message)
 errorMessage = err.message
 }
 
 throw new Error(errorMessage)
 }
 }

 const activateUser = async (userId: string) => {
 try {
 console.log('🔄 Iniciando ativação do usuário:', userId)
 const response = await apiService.activateUser(userId)
 console.log('✅ Resposta da API activateUser:', response)
 
 // Atualiza o usuário na lista local
 setUsers(prev => prev.map(user => 
 user.id === userId 
 ? { ...user, isActive: true, status: 'active' }
 : user
 ))
 
 console.log('✅ Usuário ativado com sucesso')
 
 } catch (err) {
 console.error('❌ Erro detalhado ao ativar usuário:', err)
 
 let errorMessage = 'Erro ao ativar usuário'
 
 if (err instanceof ApiError) {
 console.error('❌ ApiError:', {
 status: err.status,
 message: err.message,
 data: (err as any).data
 })
 errorMessage = err.message
 } else if (err instanceof Error) {
 console.error('❌ Error:', err.message)
 errorMessage = err.message
 }
 
 throw new Error(errorMessage)
 }
 }

 const getUserById = async (userId: string) => {
 try {
 console.log('🔄 Buscando usuário por ID:', userId)
 const response = await apiService.getUserById(userId)
 console.log('✅ Usuário encontrado:', response)
 
 return response
 
 } catch (err) {
 console.error('❌ Erro ao buscar usuário:', err)
 
 let errorMessage = 'Erro ao buscar usuário'
 
 if (err instanceof ApiError) {
 console.error('❌ ApiError:', {
 status: err.status,
 message: err.message,
 data: (err as any).data
 })
 errorMessage = err.message
 } else if (err instanceof Error) {
 console.error('❌ Error:', err.message)
 errorMessage = err.message
 }
 
 throw new Error(errorMessage)
 }
 }

 const updateUser = async (userId: string, userData: {
 name: string
 email: string
 password?: string
 phones: UserPhone[]
 dealershipRole: string
 isActive?: boolean
 avatar?: File | null
 }) => {
 try {
 console.log('🔄 Atualizando usuário:', userId, userData)
 const response = await apiService.updateUser(userId, userData)
 console.log('✅ Usuário atualizado:', response)
 
 // Atualiza o usuário na lista local
 setUsers(prev => prev.map(user => 
 user.id === userId 
 ? { 
 ...user, 
 name: userData.name,
 email: userData.email,
 phones: userData.phones,
 dealershipRole: userData.dealershipRole,
 isActive: userData.isActive ?? user.isActive,
 status: userData.isActive ? 'active' : 'inactive'
 }
 : user
 ))
 
 console.log('✅ Usuário atualizado com sucesso')
 
 } catch (err) {
 console.error('❌ Erro ao atualizar usuário:', err)
 
 let errorMessage = 'Erro ao atualizar usuário'
 
 if (err instanceof ApiError) {
 console.error('❌ ApiError:', {
 status: err.status,
 message: err.message,
 data: (err as any).data
 })
 errorMessage = err.message
 } else if (err instanceof Error) {
 console.error('❌ Error:', err.message)
 errorMessage = err.message
 }
 
 throw new Error(errorMessage)
 }
 }

 const loadMore = async () => {
 if (hasMore && !isLoading) {
 const nextPage = currentPage + 1
 setCurrentPage(nextPage)
 await fetchUsers(nextPage, true)
 }
 }

 useEffect(() => {
 fetchUsers()
 }, [])

 const refetch = async () => {
 setCurrentPage(1)
 await fetchUsers(1, false)
 }

 return {
 users,
 isLoading,
 error,
 refetch,
 createUser,
 disableUser,
 activateUser,
 getUserById,
 updateUser,
 loadMore,
 hasMore,
 currentPage,
 pagination
 }
}
